import {
  useRef,
  useContext,
  useLayoutEffect,
  KeyboardEvent,
  useEffect,
} from "react";
import { PopoverContext } from "./PopoverContext";
import { tabbable } from "tabbable";

export type dataStateType = "open" | "closed";

export function usePopoverContext() {
  const context = useContext(PopoverContext);
  const invokerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLSpanElement>(null);
  const { triggerHovered, setTriggerHovered, contentHovered } = context || {};

  useLayoutEffect(() => {
    if (!invokerRef.current) return;
    context?.setInvoker?.(invokerRef.current);
  }, [context, invokerRef]);

  useLayoutEffect(() => {
    if (!popoverRef.current) return;
    context?.setPopover?.(popoverRef.current);
    context?.setRootNode?.(popoverRef.current.getRootNode());
    if (!arrowRef.current) return;
    context?.setArrowElement?.(arrowRef.current);
  }, [context, popoverRef]);

  useEffect(() => {
    if (!context?.openOnHover) return;
    // If the popover was previously opened by hover, and is now unhovered, close the popover after 100ms
    if (!triggerHovered && !contentHovered) {
      const closeTimeout: NodeJS.Timeout = setTimeout(() => {
        context?.closePopover();
      }, 100);
      return () => {
        clearTimeout(closeTimeout);
      };
    }
    // If the popover was unopened by hover, and is now hovered, open the popover after the defined delay
    if (triggerHovered && !contentHovered) {
      if (context?.delay) {
        const openTimeout = setTimeout(() => {
          context?.openPopover();
        }, context.delay);
        return () => {
          clearTimeout(openTimeout);
        };
      } else {
        context?.openPopover();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context?.delay, context?.openOnHover, triggerHovered, contentHovered]);

  const onMouseEnterHandler = () => {
    if (!context?.openOnHover) return;
    setTriggerHovered?.(true);
  };
  const onMouseLeaveHandler = () => {
    if (!context?.openOnHover) return;
    setTriggerHovered?.(false);
  };

  const onFocusHandler = (_e: React.FocusEvent) => {
    if (context?.openOnHover) {
      context?.openPopover();
    }
  };

  const onBlurHandler = (e: React.FocusEvent) => {
    if (context?.popover?.contains(e.relatedTarget as Node)) return; // If the focus is within the popover, don't close it
    if (context?.modal && context.openState) {
      e.preventDefault();
      requestAnimationFrame(() => {
        context?.invoker?.focus();
        const focusables = tabbable(context?.popover as Element);
        if (focusables.length > 0) {
          focusables[focusables.length - 1].focus();
        }
      });
      return;
    }
    if (!context?.disableCloseOnClickOutside) {
      context?.closePopover();
    }
  };

  const onKeyDownHandler = (e: KeyboardEvent) => {
    if (!context?.openState) return;
    if (
      e.code === "Escape" &&
      !context?.disableCloseOnEscape &&
      !context?.controlled
    ) {
      context?.closePopover();
      return;
    }
  };

  const onClickHandler = () => {
    context?.invoker?.focus();
    if (context?.openOnHover) return;
    if (context?.controlled === false) {
      if (context?.openState) {
        context?.closePopover();
      } else {
        context?.openPopover();
      }
      return;
    }
  };

  if (!context || !invokerRef || !popoverRef) return;
  return {
    invoker: {
      ref: invokerRef,
      element: context.invoker,
      onMouseEnter: onMouseEnterHandler,
      onMouseLeave: onMouseLeaveHandler,
      onKeyDown: onKeyDownHandler,
      onClick: onClickHandler,
      onFocus: onFocusHandler,
      onBlur: onBlurHandler,
      "aria-haspopup": true,
      "aria-expanded": context.openState,
      "aria-details": context.popoverId,
      "data-state": context.openState ? "open" : ("closed" as dataStateType),
    },
    popover: {
      ref: popoverRef,
      element: context.popover,
      id: context.popoverId,
      onClose: context.onClose,
      show: context.openPopover,
      close: context.closePopover,
      noPadding: context.noPadding,
      style: context.popoverStyle,
      arrowRef: arrowRef,
    },
    rootNode: context.rootNode,
    onClose: context.onClose,
    open: context.openState,
    openOnHover: context.openOnHover,
    controlled: context.controlled,
    disableCloseOnClickOutside: context.disableCloseOnClickOutside,
    disableCloseOnEscape: context.disableCloseOnEscape,
    modal: context.modal,
    placement: context.placement,
    actualPlacement: context.actualPlacement,
    disableCaret: context.disableCaret,
    noPadding: context.noPadding,
    fillAvailableHeight: context.fillAvailableHeight,
    maxHeight: context.maxHeight,
    matchReferenceWidth: context.matchReferenceWidth,
    fixedWidth: context.fixedWidth,
    disableFlipFallback: context.disableFlipFallback,
    disableTriggerFocus: context.disableTriggerFocus,
    triggerHovered: context.triggerHovered,
    setTriggerHovered: context.setTriggerHovered,
    contentHovered: context.contentHovered,
    setContentHovered: context.setContentHovered,
  };
}
